<template>
    <v-col cols="12" md="6" lg="4" class="px-2 mb-2">
      <v-card>
        <v-card-title class="pb-2">
          <v-row no-gutters>
            <v-col class="text-truncate px-1">
              <span :class="$vuetify.breakpoint.mdAndDown ? 'text-subtitle-1' : 'title'">{{currentSubscriptionPlan.name}}</span>
              <span v-if="currentSubscriptionPlan.custom" class="ml-1" :class="$vuetify.breakpoint.mdAndDown ? 'text-subtitle-1' : 'title'">+ Oferta</span>
            </v-col>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn class="float-right" icon @click="showPlanInfo = true" v-on="on">
                  <v-icon color="primary">info_outline</v-icon>
                </v-btn>
              </template>
              <span>Info</span>
            </v-tooltip>
          </v-row>
        </v-card-title>
        <v-card-text class="pb-1">
          <v-row no-gutters>
            <translate>Patients</translate>
            <v-spacer></v-spacer>
            <span v-if="currentSubscriptionPlan.num_patients == 0" v-translate>Unlimited</span>
            <span v-else>{{currentSubscriptionPlan.num_patients}}</span>
            <v-progress-linear rounded :color="barColor(patientsPercentage)" height="5" :value="patientsPercentage"></v-progress-linear>
          </v-row>
          <v-row no-gutters>
            <translate>Professionals</translate>
            <v-spacer></v-spacer>
            <span v-if="currentSubscriptionPlan.num_professionals == 0" v-translate>Unlimited</span>
            <span v-else>{{currentSubscriptionPlan.num_professionals}}</span>
            <v-progress-linear rounded :color="barColor(professionalsPercentage)" height="5" :value="professionalsPercentage"></v-progress-linear>
          </v-row>
          <v-row no-gutters>
            <translate>Secretaries</translate>
            <v-spacer></v-spacer>
            <span v-if="currentSubscriptionPlan.num_secretaries == 0" v-translate>Unlimited</span>
            <span v-else>{{currentSubscriptionPlan.num_secretaries}}</span>
            <v-progress-linear rounded :color="barColor(secretariesPercentage)" height="5" :value="secretariesPercentage"></v-progress-linear>
          </v-row>
          <v-row no-gutters>
            <translate>Disk space</translate>
            <v-spacer></v-spacer>
            <span v-if="currentSubscriptionPlan.disk_space == 0" v-translate>Unlimited</span>
            <span v-else>{{currentSubscriptionPlan.disk_space}}GB</span>
            <v-progress-linear rounded :color="barColor(diskSpacePercentage)" height="5" :value="diskSpacePercentage"></v-progress-linear>
          </v-row>
          <v-row no-gutters class="mt-2 font-weight-medium">
            <template v-if="currentSubscriptionPlan.days_left > 0">
              <span v-translate>Expires in</span>
              <span class="mx-1">{{currentSubscriptionPlan.days_left}}</span>
              <span v-translate>days</span>!
            </template>
            <span v-else class="red--text font-weight-bold"><translate>Expired</translate>!</span>
            <v-progress-linear class="mt-1" rounded :color="percentageColor(currentSubscriptionPlan.percentage)" height="10" :value="currentSubscriptionPlan.percentage" striped></v-progress-linear>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn key="upgrade" color="primary" text @click="showUpgradeOptions = true" :disabled="!canUpgrade">
						<translate>Upgrade</translate>
					</v-btn>
          <v-spacer></v-spacer>
          <v-btn key="extend" color="primary" text @click="showMonthPricing = true" :disabled="!canExtend">
						<translate>Extend</translate>
					</v-btn>
        </v-card-actions>
      </v-card>
      <v-dialog v-model="showPlanInfo" width="40%">
				<v-card>
					<v-card-title class="primary--text mb-2 secondary">
						<translate>Plan Info</translate>
						<span class="mx-1">-</span>
						{{currentSubscriptionPlan.name}}
					</v-card-title>
					<v-card-text>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th v-translate></th>
                    <th v-translate>N. Max.</th>
                    <th v-translate>N. Atual</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><translate>Patients</translate></td>
                    <td>
                      <span v-if="currentSubscriptionPlan.num_patients == 0" v-translate>Unlimited</span>
                      <span v-else>{{currentSubscriptionPlan.num_patients}}</span>
                    </td>
                    <td>{{ spaceParams.num_patients }}</td>
                  </tr>
                  <tr>
                    <td><translate>Professionals</translate></td>
                    <td>
                      <span v-if="currentSubscriptionPlan.num_professionals == 0" v-translate>Unlimited</span>
                      <span v-else>{{currentSubscriptionPlan.num_professionals}}</span>
                    </td>
                    <td>{{ spaceParams.num_professionals }}</td>
                  </tr>
                  <tr>
                    <td><translate>Secretaries</translate></td>
                    <td>
                      <span v-if="currentSubscriptionPlan.num_secretaries == 0" v-translate>Unlimited</span>
                      <span v-else>{{currentSubscriptionPlan.num_secretaries}}</span>
                    </td>
                    <td>{{ spaceParams.num_secretaries }}</td>
                  </tr>
                  <tr>
                    <td><translate>Disk space</translate></td>
                    <td>
                      <span v-if="currentSubscriptionPlan.disk_space == 0" v-translate>Unlimited</span>
                      <span v-else>{{currentSubscriptionPlan.disk_space}}GB</span>
                    </td>
                    <td>{{ spaceParams.disk_space_converted }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
					<v-divider></v-divider>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn color="primary" text @click="showPlanInfo = false">
							<translate>Close</translate>
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
      <v-dialog v-model="showUpgradeOptions" width="450px">
				<v-card>
					<v-card-title class="primary--text mb-2 secondary">
						<translate>Upgrade</translate>
					</v-card-title>
					<v-card-text>
            <v-row no-gutters align="center" justify="center" class="mb-2">
							<v-tabs fixed-tabs v-model="selectedUpgradePlan">
								<v-tab v-for="plan in subscriptionPlansToUpgrade" :key="plan.id" :href="'#'+plan.code">{{plan.name}}</v-tab>
							</v-tabs>
							<v-container fluid>
									<v-tabs-items v-model="selectedUpgradePlan" class="mt-2">
										<v-tab-item :transition="false" :reverse-transition="false" v-for="plan in subscriptionPlansToUpgrade" :key="plan.id" :value="plan.code">
                      <template v-if="$store.getters.upgradeCosts(plan, currentSubscriptionPlan)">
                        <v-card class="mb-3 ma-2">
                          <v-card-text>
                            <p class="mb-1 font-weight-bold">Custos de upgrade de plano:</p>
                            <div v-if="$store.getters.upgradeCosts(plan, currentSubscriptionPlan)">
                              <span class="mr-1">-{{currentSubscriptionPlan.name}}:</span>
                              <span class="mr-1">{{$store.getters.upgradeCosts(plan, currentSubscriptionPlan).days}} <translate>days</translate></span>
                              <span>= {{$store.getters.upgradeCosts(plan, currentSubscriptionPlan).price}}€</span>
                            </div>
                          </v-card-text>
                        </v-card>
												<v-divider class="my-3"></v-divider>
												<v-row no-gutters class="text-body-1">
													Total
													<v-spacer></v-spacer>
													<span class="font-weight-bold">{{getTotalPriceUpgrade(plan)}}€</span>
												</v-row>
                        <!-- <template v-if="plan.code == 'basic'">
                          <v-form ref="basicPlanModuleFormUpgrade">
                            <v-select
                              v-model="basicPlanModule"
                              :items="[{id: 1, name: 'Fitoterapia'}, {id: 2, name: 'Medicina Tradicional Chinesa'}, {id: 3, name: 'Osteopatia'}, {id: 4, name: 'Genérico'}]"
                              :label="$gettext('Module')"
                              outlined
                              color="primary"
                              class="mt-4"
                              item-value="id"
                              item-text="name"
                              :rules="moduleRequired"
                              return-object
                            ></v-select>
                          </v-form>
                          <b>Nota:</b> Posteriormente poderá alterar o módulo.
                        </template> -->
                      </template>
										</v-tab-item>
									</v-tabs-items>
							</v-container>
						</v-row>
          </v-card-text>
					<v-divider></v-divider>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn color="primary" text @click="upgrade" :disabled="!validUpgradePlan">
							<translate>Upgrade Now</translate>
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
      <v-dialog v-model="showMonthPricing" width="450px" scrollable>
				<v-card>
					<v-card-title class="title primary--text secondary">
						<translate>Pricing</translate>
					</v-card-title>
					<v-card-text class="py-3">
						<v-row no-gutters align="center" justify="center" class="mb-2">
							<v-tabs fixed-tabs v-model="selectedExtendPlan">
								<v-tab v-for="plan in subscriptionPlans" :key="plan.id" :href="'#'+plan.code">{{plan.name}}</v-tab>
							</v-tabs>
							<v-container fluid>
								<v-tabs-items v-model="selectedExtendPlan" class="mt-2">
									<v-tab-item :transition="false" :reverse-transition="false" v-for="plan in subscriptionPlans" :key="plan.id" :value="plan.code">
										<template v-if="canBuyPlan(plan.code)">
											<template v-if="$store.getters.upgradeCosts(plan, currentSubscriptionPlan)">
												<v-card class="mb-3 ma-2">
													<v-card-text>
														<p class="mb-1 font-weight-bold">Custos adicionais de upgrade de plano:</p>
                            <div v-if="$store.getters.upgradeCosts(plan, currentSubscriptionPlan)">
															<span class="mr-1">-{{currentSubscriptionPlan.name}}:</span>
															<span class="mr-1">{{$store.getters.upgradeCosts(plan, currentSubscriptionPlan).days}} <translate>days</translate></span>
															<span>= {{$store.getters.upgradeCosts(plan, currentSubscriptionPlan).price}}€</span>
														</div>
													</v-card-text>
												</v-card>
											</template>
											<v-radio-group v-model="duration" class="mt-0" hide-details>
												<template v-for="(pricingPlan, i) in plan.pricing_plans">
													<v-radio
														color="primary"
														:value="pricingPlan.months"
														:key="pricingPlan.months"
														class="ma-0"
													>
														<template v-slot:label>
															<div style="width: 340px">
																<PricingPlan :pricingPlan="pricingPlan" :module="{code: 'fito', discount_percentage: 0, daily_price: 2}" type="monthly" :plan="plan" :upgrade="$store.getters.upgradeCosts(plan, currentSubscriptionPlan)" />
															</div>
														</template>
													</v-radio>
													<v-divider
														v-if="i != plan.pricing_plans.length - 1"
														:key="pricingPlan.months * 13"
														class="my-3"
													></v-divider>
												</template>
											</v-radio-group>
											<v-divider class="my-3"></v-divider>
											<v-row no-gutters class="text-body-1">
												Total
												<v-spacer></v-spacer>
												<span class="font-weight-bold">{{getTotalPriceExtend(plan)}}€</span>
											</v-row>
                      <!-- <template v-if="plan.code == 'basic'">
                        <v-form ref="basicPlanModuleFormExtend">
                          <v-select
                            v-model="basicPlanModule"
                            :items="[{id: 1, name: 'Fitoterapia'}, {id: 2, name: 'Medicina Tradicional Chinesa'}, {id: 3, name: 'Osteopatia'}, {id: 4, name: 'Genérico'}]"
                            :label="$gettext('Module')"
                            outlined
                            color="primary"
                            class="mt-4"
                            item-value="id"
                            item-text="name"
                            :rules="moduleRequired"
                            return-object
                          ></v-select>
                        </v-form>
                        <b>Nota:</b> Posteriormente poderá alterar o módulo.
                      </template> -->
										</template>
										<div v-else>
											<translate>You don't fit the requirements to buy this plan.</translate>
										</div>
									</v-tab-item>
								</v-tabs-items>
							</v-container>
						</v-row>
					</v-card-text>
					<v-divider></v-divider>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn color="primary" text @click="buyPlanNow" :disabled="!validExtendPlan">
							<translate>Buy Now</translate>
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
    </v-col>
</template>

<script>
import PricingPlan from "@/components/modules/PricingPlan.vue";

export default {
  name: "SubscriptionPlanCard",
  props: ["subscriptionPlans", "spaceParams", "currentSubscriptionPlan"],
  components: {
    PricingPlan
  },
  data: () => ({
    showPlanInfo: false,
    showUpgradeOptions: false,
    showMonthPricing: false,
    selectedUpgradePlan: "",
    selectedExtendPlan: "",
    duration: 1,
    basicPlanModule: null
  }),
  computed: {
    subscriptionPlansToUpgrade() {
      if(this.currentSubscriptionPlan.code == 'free') return this.subscriptionPlans;
      else if(this.currentSubscriptionPlan.code == 'student') return this.subscriptionPlans;
      else if(this.currentSubscriptionPlan.code == 'basic') return this.subscriptionPlans.filter(sp => sp.code == 'professional' || sp.code == 'corporative');
      else if(this.currentSubscriptionPlan.code == 'professional') return this.subscriptionPlans.filter(sp => sp.code == 'corporative');
      else return [];
    },
    patientsPercentage() {
      if(this.currentSubscriptionPlan.num_patients == 0) return 0;
      return (this.spaceParams.num_patients * 100) / this.currentSubscriptionPlan.num_patients;
    },
    professionalsPercentage() {
      if(this.currentSubscriptionPlan.num_professionals == 0) return 0;
      return (this.spaceParams.num_professionals * 100) / this.currentSubscriptionPlan.num_professionals;
    },
    secretariesPercentage() {
      if(this.currentSubscriptionPlan.num_secretaries == 0) return 0;
      return (this.spaceParams.num_secretaries * 100) / this.currentSubscriptionPlan.num_secretaries;
    },
    diskSpacePercentage() {
      if(this.currentSubscriptionPlan.disk_space == 0) return 0;
      if(this.currentSubscriptionPlan.disk_space_bytes == 0) return 0;
      return (this.spaceParams.disk_space_bytes * 100) / (this.currentSubscriptionPlan.disk_space * 1000000000);
    },
    canUpgrade() {
      if(!this.$store.getters.hasPlan) return false;
      if(this.currentSubscriptionPlan.code == 'corporative' || this.currentSubscriptionPlan.code == 'consultant' || this.currentSubscriptionPlan.code == 'free' || this.currentSubscriptionPlan.code == 'student') return false;
      return true;
    },
    canExtend() {
      if(this.currentSubscriptionPlan.code == 'consultant' || this.currentSubscriptionPlan.code == 'free') return true;
      return true;
    },
    moduleRequired() {
			return [v => !!v || this.$gettext("Required")];
		},
    validExtendPlan() {
      if(!this.selectedExtendPlan) return false;
      if(this.canBuyPlan(this.selectedExtendPlan)) return true;
      else return false;
    },
    validUpgradePlan() {
      if(!this.selectedUpgradePlan) return false;
      else return true;
    }
  },
  methods: {
    barColor(percentage) {
			if (percentage < 50) {
				return "green lighten-2";
			} else if (percentage < 80) {
				return "orange lighten-2";
			} else {
				return "red lighten-2";
			}
    },
    percentageColor(percentage) {
      if (percentage < 30) {
				return "red lighten-2";
			} else if (percentage < 60) {
				return "orange lighten-2";
			} else {
				return "green lighten-2";
			}
    },
    getTotalPriceUpgrade(plan) {
      let updateCosts = this.$store.getters.upgradeCosts(plan, this.currentSubscriptionPlan);
			return updateCosts.price;
		},
    addUpgradeToCart() {
      let plan;
			for(let i=0; i<this.subscriptionPlans.length; i++) {
				if(this.subscriptionPlans[i].code == this.selectedUpgradePlan) plan = this.subscriptionPlans[i];
      }
      if(this.selectedUpgradePlan != 'basic') this.basicPlanModule = null;
      let price = this.getTotalPriceUpgrade(plan);
			this.$store.dispatch("ADD_ITEM", {
        type: "subscription",
        upgrade: true,
        months: 0,
        days: 0,
				price: price,
				priceWithDiscount: price,
        plan: plan,
        module: this.basicPlanModule
			});
			this.showUpgradeOptions = false;
    },
    upgrade() {
      if(this.validateUpgradePlan()) {
        this.addUpgradeToCart();
        this.$router.push({ name: "cart" });
      }
    },
    canBuyPlan(code) {
			if(this.currentSubscriptionPlan == null) return true;
			else {
				if(this.currentSubscriptionPlan.code == 'free') return this.checkParams(code);
        if(this.currentSubscriptionPlan.code == 'student') return this.checkParams(code);
				if(this.currentSubscriptionPlan.code == 'basic') return this.checkParams(code);
				if(this.currentSubscriptionPlan.code == 'professional') {
					if(code == 'basic') return false;
					else return this.checkParams(code);
				}
				if(this.currentSubscriptionPlan.code == 'corporative') {
					if(code != 'corporative') return false;
					else return true
				}
			}
    },
    checkParams(code) {
			let plan;
			for(let i=0; i<this.subscriptionPlans.length; i++) {
				if(this.subscriptionPlans[i].code == code) plan = this.subscriptionPlans[i];
			}
			if(plan.num_patients != 0) {
				if(this.spaceParams.num_patients > plan.num_patients) return false;
			}
			if(plan.num_professionals != 0) {
				if(this.spaceParams.num_professionals > plan.num_professionals) return false;
			}
			if(plan.num_secretaries != 0) {
				if(this.spaceParams.num_secretaries > plan.num_secretaries) return false;
			}
			if(plan.disk_space != 0) {
				if(this.spaceParams.disk_space_bytes > plan.disk_space * 1000000000) return false;
			}
			return true;
    },
    getTotalPriceExtend(plan) {
			let planPriceInfo = this.$store.getters.monthlyPlanPrice(this.duration, plan);
			let planPrice = planPriceInfo.priceWithDiscount;
			let updateCosts = this.$store.getters.upgradeCosts(plan, this.currentSubscriptionPlan);
      
      if(updateCosts) return Math.round(((planPrice + updateCosts.price) + Number.EPSILON) * 100) / 100;
			else return planPrice;
    },
    validateExtendPlan() {
      if(!this.selectedExtendPlan) return false;
      else return this.canBuyPlan(this.selectedExtendPlan);
      //if(this.canBuyPlan(this.selectedExtendPlan)) {
        //if(this.selectedExtendPlan != 'basic') return true;
        //else {
        //  if(!this.$refs.basicPlanModuleFormExtend) return false;
        //  return this.$refs.basicPlanModuleFormExtend[0].validate();
        //}
      //} else return false;
    },
    validateUpgradePlan() {
      return this.selectedUpgradePlan;
      //if(!this.selectedUpgradePlan) return false;
      //if(this.selectedUpgradePlan != 'basic') return true;
      //else {
      //  if(!this.$refs.basicPlanModuleFormUpgrade) return false;
      //  return this.$refs.basicPlanModuleFormUpgrade[0].validate();
      //}
    },
    buyPlanNow() {
      if(this.validateExtendPlan()) {
        this.addPlanToCart();
        this.$router.push({ name: "cart" });
      }
    },
    addPlanToCart() {
			let pricePlan;
			let plan;
			for(let i=0; i<this.subscriptionPlans.length; i++) {
				if(this.subscriptionPlans[i].code == this.selectedExtendPlan) plan = this.subscriptionPlans[i];
			}
			pricePlan = this.$store.getters.monthlyPlanPrice(
				this.duration,
				plan
      );
      if(this.selectedExtendPlan != 'basic') this.basicPlanModule = null;
			let price = this.getTotalPriceExtend(plan);
			this.$store.dispatch("ADD_ITEM", {
        type: "subscription",
				upgrade: false,
				days: pricePlan.days,
				months: pricePlan.months,
				price: price,
				priceWithDiscount: price,
        plan: plan,
        module: this.basicPlanModule
			});
			this.showMonthPricing = false;
    },
    // freeModule() {
		// 	axios
		// 		.post("/modules/" + this.module.module.id + "/free_trial")
		// 		.then(response => {
		// 			if (response.data.result === "ok") {
		// 				this.refresh();
		// 			}
		// 		})
		// 		.catch(error => {
		// 			console.log(error);
		// 		});
    // }
    // hasFreeModule() {
		// 	if(this.owned) return false;
		// 	if(this.module.module.code == 'base' && this.payments.length > 0) return false;
		// 	for(let i in this.payments) {
		// 		for(let x in this.payments[i].modules) {
		// 			if(this.payments[i].modules[x].code == this.module.module.code && this.payments[i].free) return false;
		// 		}
		// 	}
		// 	return true;
		// }
  }
}
</script>